import Alert from "@mui/lab/Alert";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Snackbar,
	SnackbarCloseReason,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { removeAsset } from "../../repositories/AssetRepository";

type Props = {
	selectedAssetId: number | null;
	selectedAssetName: string | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const RemoveAssetDialog = ({
	selectedAssetId,
	selectedAssetName,
	show,
	onHide,
	suffixTitle,
}: Props) => {
	const [assetRemovedSuccess, setAssetRemovedSuccess] =
		useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const handleCancel = () => {
		setIsSubmitLoading(false);
		onHide(false);
	};

	const handleClose = (
		event: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setAssetRemovedSuccess(false);
	};

	const deleteAsset = useMutation({
		mutationFn: removeAsset,
		onSuccess: () => {
			setAssetRemovedSuccess(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const handleRemoveAsset = async () => {
		setIsSubmitLoading(true);
		await deleteAsset.mutate({
			AssetId: selectedAssetId!,
			IsActive: false,
		});
	};

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Delete Asset${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth
				maxWidth="sm"
				open={show}
				onClose={() => handleCancel()}
				disableEnforceFocus
				sx={{
					"& .input-group-text": {
						display: "none",
					},
					"& .MuiDialogContent-dividers": {
						padding: 0,
					},
				}}
			>
				<DialogTitle id="responsive-dialog-title">{"WARNING"}</DialogTitle>
				<DialogContent>
					<DialogContentText style={{ padding: 0, margin: 0 }}>
						Be careful, once deleted you will not be able to recover this asset{" "}
						<b>({selectedAssetName})</b>, are you sure you want to delete?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px" }}>
					<Button onClick={() => onHide(false)} autoFocus color="inherit">
						CANCEL
					</Button>
					<Button
						autoFocus
						color="secondary"
						startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
						onClick={handleRemoveAsset}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={assetRemovedSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="info" onClose={handleClose}>
					Asset removed succesfully
				</Alert>
			</Snackbar>
		</>
	);
};
